// ./application-steps/StepContent8.js

import React, { useState, useEffect, memo, useRef } from "react";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import Divider from "@mui/material/Divider";
import Alert from "@mui/material/Alert";
import Stack from "@mui/material/Stack";

import FormRow from "../../helpers/FormRow";
import FormInput from "../../helpers/FormInput";
import FormRadioGroup from "../../helpers/FormRadioGroup";
import { calculateTaxYear } from "../../utils/dateHelpers";

const BURSARY_FIELDS = [
  "bursaryOptOut",
  "bursary",
  "bursary_resides_with",
  "bursary_resides_with_other",
  "bursary_gross_income_father",
  "bursary_gross_income_mother",
  "bursary_gross_income_stepfather",
  "bursary_gross_income_stepmother",
  "bursary_gross_income_applicant",
  "bursary_gross_income_other",
  "bursary_gross_income_other_explain",
  "bursary_reason",
  "bursary_siblings",
  "bursary_signing_date",
  "bursary_student_signing",
];

const EMPTY_BURSARY_VALUES = {
  bursary_resides_with: "",
  bursary_resides_with_other: "",
  bursary_gross_income_father: "",
  bursary_gross_income_mother: "",
  bursary_gross_income_stepfather: "",
  bursary_gross_income_stepmother: "",
  bursary_gross_income_applicant: "",
  bursary_gross_income_other: "",
  bursary_gross_income_other_explain: "",
  bursary_reason: "",
  bursary_siblings: "",
  bursary_signing_date: "",
  bursary_student_signing: "",
};

const pick = (obj, keys) =>
  keys.reduce((acc, k) => {
    acc[k] = obj?.[k];
    return acc;
  }, {});

function StepContent8({
  formData,
  setFormData,
  stepErrors,
  handleChange,
  isReadOnly,
  scholarshipYear,
  setActiveStep,
  onBursaryOptOutChange,
}) {
  const [totalIncome, setTotalIncome] = useState(0);
  const taxYear = calculateTaxYear(scholarshipYear);

  const isOptedOut = !!formData.bursaryOptOut;
  const [isSavingOptOut, setIsSavingOptOut] = useState(false);
  const prevSnapshotRef = useRef(null);

  useEffect(() => {
    const total =
      (parseFloat(formData.bursary_gross_income_father) || 0) +
      (parseFloat(formData.bursary_gross_income_mother) || 0) +
      (parseFloat(formData.bursary_gross_income_stepfather) || 0) +
      (parseFloat(formData.bursary_gross_income_stepmother) || 0) +
      (parseFloat(formData.bursary_gross_income_applicant) || 0) +
      (parseFloat(formData.bursary_gross_income_other) || 0);
    setTotalIncome(total);
  }, [
    formData.bursary_gross_income_father,
    formData.bursary_gross_income_mother,
    formData.bursary_gross_income_stepfather,
    formData.bursary_gross_income_stepmother,
    formData.bursary_gross_income_applicant,
    formData.bursary_gross_income_other,
  ]);

  const moneyRegex = /^\d*(\.\d{0,2})?$/;

  const handleNumberChange = (e) => {
    const { name, value } = e.target;
    if (value === "" || moneyRegex.test(value)) {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  const formattedTotalIncome = Number.isFinite(totalIncome)
    ? totalIncome.toLocaleString("en-CA", { maximumFractionDigits: 2 })
    : "0";

  const handleIntegerChange = (e) => {
    const { name, value } = e.target;
    const regex = /^\d*$/;
    if (value === "" || regex.test(value)) {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleOptOutToggle = async (e) => {
    const checked = e.target.checked;
    if (isSavingOptOut) return;

    setIsSavingOptOut(true);

    setFormData((prev) => {
      prevSnapshotRef.current = pick(prev, BURSARY_FIELDS);

      const next = {
        ...prev,
        bursaryOptOut: checked,
        bursary: checked ? false : true,
      };

      return checked ? { ...next, ...EMPTY_BURSARY_VALUES } : next;
    });

    try {
      await onBursaryOptOutChange?.(checked);
      prevSnapshotRef.current = null;
    } catch (err) {
      console.error(err);

      const snap = prevSnapshotRef.current;
      if (snap) {
        setFormData((prev) => ({
          ...prev,
          ...snap,
        }));
      }
    } finally {
      setIsSavingOptOut(false);
    }
  };

  const getStatus = (field) => {
    if (stepErrors[field]) return "error";
    if (formData[field]) return "success";
    return "";
  };

  const IntroText =
    'Bursaries provide financial assistance to students to help with education-related costs and are awarded based on financial need rather than grades or academic performance. Some bursaries also consider personal circumstances, community involvement, or specific areas of study. We understand that every student\'s situation is unique and encourage you to share any details that will help us understand your need for support. All responses will be treated with the strictest confidence. Students are automatically enrolled to ensure everyone has access to support. If you prefer, you may choose to "Opt Out".';

  return (
    <Paper
      elevation={0}
      sx={{
        p: { xs: 2, sm: 3 },
        borderRadius: 2,
        border: "1px solid",
        borderColor: "divider",
        bgcolor: "background.paper",
      }}
      className="form-group"
    >
      {/* Header */}
      <Stack spacing={1} sx={{ mb: 2 }}>
        <Typography variant="h5" fontWeight={800} textAlign="center">
          Bursary Application
        </Typography>
        <Typography
          variant="body2"
          sx={{ color: "text.secondary", mx: "auto" }}
          textAlign="left"
        >
          {IntroText}
        </Typography>
      </Stack>

      {/* Notice + Opt-out */}
      <Stack spacing={2} sx={{ mb: 2 }}>
        <Alert severity="info" variant="outlined">
          <Typography variant="body2">
            <strong>PLEASE NOTE:</strong> Completion of all parts of this
            section is required. If insufficient background information is
            given, the awards committee may not be able to determine eligibility
            for a bursary.
          </Typography>
          <Typography variant="body2" sx={{ mt: 1 }}>
            <strong>
              Students who do not complete this form and include it with their
              application will not be considered for bursaries.
            </strong>
          </Typography>
        </Alert>

        <Paper
          elevation={0}
          sx={{
            p: 1.5,
            borderRadius: 2,
            border: "1px solid",
            borderColor: "divider",
            bgcolor: "background.default",
          }}
        >
          <FormControlLabel
            control={
              <Checkbox
                checked={isOptedOut}
                onChange={handleOptOutToggle}
                disabled={isReadOnly || isSavingOptOut}
              />
            }
            label={
              <Box sx={{ ml: 2 }}>
                <Typography variant="subtitle1" fontWeight={700}>
                  Opt out of bursary consideration
                </Typography>
                <Typography variant="body2" sx={{ color: "text.secondary" }}>
                  If you opt out, you will not be considered for bursaries and
                  the bursary section will be hidden.
                </Typography>
              </Box>
            }
          />
        </Paper>
      </Stack>

      {isOptedOut ? (
        <Box sx={{ mt: 2 }}>
          <Alert severity="warning" variant="outlined">
            You have opted out of bursary consideration.
          </Alert>

          <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
            <Button
              variant="contained"
              color="info"
              onClick={() => setActiveStep((prev) => prev + 1)}
            >
              Continue
            </Button>
          </Box>
        </Box>
      ) : (
        <>
          <Divider sx={{ my: 2 }} />

          {/* Section: Household */}
          <Box sx={{ mb: 2 }}>
            <Typography variant="h6" fontWeight={800} sx={{ mb: 1 }}>
              Household
            </Typography>

            <FormRow spacing={2} className="form-row">
              <Grid item xs={12}>
                <FormRadioGroup
                  label="Lives with"
                  name="bursary_resides_with"
                  value={formData.bursary_resides_with}
                  onChange={handleChange("bursary_resides_with")}
                  options={[
                    { label: "Both Parents", value: "Both Parents" },
                    { label: "Mother", value: "Mother" },
                    { label: "Father", value: "Father" },
                    { label: "Other", value: "Other" },
                  ]}
                  row
                  isReadOnly={isReadOnly}
                  error={Boolean(stepErrors.bursary_resides_with)}
                  helperText={stepErrors.bursary_resides_with || ""}
                  status={getStatus("bursary_resides_with")}
                />
              </Grid>
            </FormRow>

            {formData.bursary_resides_with === "Other" && (
              <FormRow spacing={2}>
                <Grid item xs={12}>
                  <FormInput
                    label="Who do you live with?"
                    name="bursary_resides_with_other"
                    value={formData.bursary_resides_with_other}
                    onChange={handleChange("bursary_resides_with_other")}
                    required
                    disabled={isReadOnly}
                    error={Boolean(stepErrors.bursary_resides_with_other)}
                    helperText={stepErrors.bursary_resides_with_other || ""}
                    status={getStatus("bursary_resides_with_other")}
                  />
                </Grid>
              </FormRow>
            )}
          </Box>

          <Divider sx={{ my: 2 }} />

          {/* Section: Income */}
          <Box sx={{ mb: 2 }}>
            <Box sx={{ mb: 1 }}>
              <Typography variant="h6" fontWeight={800}>
                Income
              </Typography>
              <Typography variant="body2" sx={{ color: "text.secondary" }}>
                Enter amounts from <strong>Line 150</strong> of your {taxYear}{" "}
                Income Tax Return for each person listed below.
              </Typography>
            </Box>

            <FormRow spacing={2}>
              <Grid item xs={12} md={6}>
                <FormInput
                  label="Father/Guardian Income"
                  name="bursary_gross_income_father"
                  value={formData.bursary_gross_income_father}
                  onChange={handleNumberChange}
                  disabled={isReadOnly}
                  InputProps={{ startAdornment: <span>$</span> }}
                  error={Boolean(stepErrors.bursary_gross_income_father)}
                  helperText={stepErrors.bursary_gross_income_father || ""}
                  status={getStatus("bursary_gross_income_father")}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormInput
                  label="Mother/Guardian Income"
                  name="bursary_gross_income_mother"
                  value={formData.bursary_gross_income_mother}
                  onChange={handleNumberChange}
                  disabled={isReadOnly}
                  InputProps={{ startAdornment: <span>$</span> }}
                  error={Boolean(stepErrors.bursary_gross_income_mother)}
                  helperText={stepErrors.bursary_gross_income_mother || ""}
                  status={getStatus("bursary_gross_income_mother")}
                />
              </Grid>
            </FormRow>

            <FormRow spacing={2}>
              <Grid item xs={12} md={6}>
                <FormInput
                  label="Step-Father Income"
                  name="bursary_gross_income_stepfather"
                  value={formData.bursary_gross_income_stepfather}
                  onChange={handleNumberChange}
                  disabled={isReadOnly}
                  InputProps={{ startAdornment: <span>$</span> }}
                  error={Boolean(stepErrors.bursary_gross_income_stepfather)}
                  helperText={stepErrors.bursary_gross_income_stepfather || ""}
                  status={getStatus("bursary_gross_income_stepfather")}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormInput
                  label="Step-Mother Income"
                  name="bursary_gross_income_stepmother"
                  value={formData.bursary_gross_income_stepmother}
                  onChange={handleNumberChange}
                  disabled={isReadOnly}
                  InputProps={{ startAdornment: <span>$</span> }}
                  error={Boolean(stepErrors.bursary_gross_income_stepmother)}
                  helperText={stepErrors.bursary_gross_income_stepmother || ""}
                  status={getStatus("bursary_gross_income_stepmother")}
                />
              </Grid>
            </FormRow>

            <FormRow spacing={2}>
              <Grid item xs={12} md={6}>
                <FormInput
                  label="Applicant Income"
                  name="bursary_gross_income_applicant"
                  value={formData.bursary_gross_income_applicant}
                  onChange={handleNumberChange}
                  disabled={isReadOnly}
                  InputProps={{ startAdornment: <span>$</span> }}
                  error={Boolean(stepErrors.bursary_gross_income_applicant)}
                  helperText={stepErrors.bursary_gross_income_applicant || ""}
                  status={getStatus("bursary_gross_income_applicant")}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormInput
                  label="Other Financial Support"
                  name="bursary_gross_income_other"
                  value={formData.bursary_gross_income_other}
                  onChange={handleNumberChange}
                  disabled={isReadOnly}
                  InputProps={{ startAdornment: <span>$</span> }}
                  error={Boolean(stepErrors.bursary_gross_income_other)}
                  helperText={stepErrors.bursary_gross_income_other || ""}
                  status={getStatus("bursary_gross_income_other")}
                />
              </Grid>
            </FormRow>

            {parseFloat(formData.bursary_gross_income_other) > 0 && (
              <FormRow spacing={2}>
                <Grid item xs={12}>
                  <FormInput
                    label="Explain other financial support"
                    name="bursary_gross_income_other_explain"
                    value={formData.bursary_gross_income_other_explain}
                    onChange={handleChange(
                      "bursary_gross_income_other_explain"
                    )}
                    required
                    disabled={isReadOnly}
                    error={Boolean(
                      stepErrors.bursary_gross_income_other_explain
                    )}
                    helperText={
                      stepErrors.bursary_gross_income_other_explain || ""
                    }
                    status={getStatus("bursary_gross_income_other_explain")}
                  />
                </Grid>
              </FormRow>
            )}

            <FormRow spacing={2}>
              <Grid item xs={12}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    border: "1px solid",
                    borderColor: "divider",
                    bgcolor: "background.default",
                  }}
                >
                  <Typography
                    variant="subtitle2"
                    sx={{ color: "text.secondary" }}
                  >
                    Total Family Income
                  </Typography>
                  <Typography variant="h5" fontWeight={900} sx={{ mt: 0.5 }}>
                    ${formattedTotalIncome}
                  </Typography>

                  {/* Keep the hidden field for form consistency */}
                  <FormInput
                    label="Total Income"
                    name="bursary_total_income"
                    value={formattedTotalIncome}
                    disabled
                    required
                    InputProps={{
                      readOnly: true,
                      startAdornment: <span>$</span>,
                    }}
                    status="success"
                    sx={{ display: "none" }}
                  />
                </Paper>
              </Grid>
            </FormRow>
          </Box>

          <Divider sx={{ my: 2 }} />

          {/* Section: Need */}
          <Box sx={{ mb: 2 }}>
            <Typography variant="h6" fontWeight={800} sx={{ mb: 1 }}>
              Financial Need
            </Typography>

            <FormRow spacing={2}>
              <Grid item xs={12}>
                <FormInput
                  label="Describe your financial need. Include any factors that may qualify you for a bursary."
                  name="bursary_reason"
                  value={formData.bursary_reason}
                  onChange={handleChange("bursary_reason")}
                  multiline
                  rows={5}
                  required
                  disabled={isReadOnly}
                  error={Boolean(stepErrors.bursary_reason)}
                  helperText={stepErrors.bursary_reason || ""}
                  status={getStatus("bursary_reason")}
                />
              </Grid>
            </FormRow>

            <FormRow spacing={2}>
              <Grid item xs={12} sm={6}>
                <FormInput
                  label="Number of siblings at home"
                  name="bursary_siblings"
                  type="text"
                  value={formData.bursary_siblings}
                  onChange={handleIntegerChange}
                  disabled={isReadOnly}
                  error={Boolean(stepErrors.bursary_siblings)}
                  helperText={stepErrors.bursary_siblings || ""}
                  status={getStatus("bursary_siblings")}
                  InputProps={{
                    inputProps: { inputMode: "numeric", pattern: "[0-9]*" },
                  }}
                />
              </Grid>
            </FormRow>
          </Box>

          <Divider sx={{ my: 2 }} />

          {/* Section: Certification */}
          <Box sx={{ mb: 1 }}>
            <Typography variant="h6" fontWeight={800} sx={{ mb: 0.5 }}>
              Certification
            </Typography>
            <Typography variant="body2" sx={{ color: "text.secondary" }}>
              I hereby declare that all of the above information is true and
              correct. I authorize the District Awards Committee to verify any
              or all of the above statements, which may be reviewed by the
              selection committee or donors as required.
            </Typography>

            <FormRow spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12} sm={6}>
                <FormInput
                  label="Date"
                  name="bursary_signing_date"
                  type="date"
                  value={formData.bursary_signing_date}
                  onChange={handleChange("bursary_signing_date")}
                  InputLabelProps={{ shrink: true }}
                  required
                  disabled={isReadOnly}
                  error={Boolean(stepErrors.bursary_signing_date)}
                  helperText={stepErrors.bursary_signing_date || ""}
                  status={getStatus("bursary_signing_date")}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormInput
                  label="Type your name"
                  name="bursary_student_signing"
                  value={formData.bursary_student_signing}
                  onChange={handleChange("bursary_student_signing")}
                  required
                  disabled={isReadOnly}
                  error={Boolean(stepErrors.bursary_student_signing)}
                  helperText={stepErrors.bursary_student_signing || ""}
                  status={getStatus("bursary_student_signing")}
                />
              </Grid>
            </FormRow>
          </Box>
        </>
      )}
    </Paper>
  );
}

export default memo(StepContent8);
